import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Headphones, Building2, User, AlertCircle } from "lucide-react";
import SelectField from "../ui/SelectField";
import InputField from "../ui/InputField";
import TextAreaField from "../ui/TextAreaField";

const API = process.env.REACT_APP_API_URL;

const TIPOS = [
  "Incidencia",
  "Consulta técnica",
  "Capacitación",
  "Solicitud administrativa",
  "Otro",
];
const PRIORIDADES = ["Baja", "Media", "Alta", "Crítica"];

export default function SoporteFormModal({ onClose, onCreado }) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    cliente: "",
    sede: "",
    area: "",
    equipo: "",
    tipo: "Incidencia",
    prioridad: "Media",
    asunto: "",
    descripcion: "",
    solicitante: { nombre: "", email: "", telefono: "", cargo: "" },
  });

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers })
      .then(({ data }) => setClientes(data || []))
      .catch(() => setClientes([]));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!form.cliente) {
      setSedes([]);
      setAreas([]);
      setEquipos([]);
      setForm((prev) => ({ ...prev, sede: "", area: "", equipo: "" }));
      return;
    }

    setForm((prev) => ({ ...prev, sede: "", area: "", equipo: "" }));

    axios
      .get(`${API}/sedes/${form.cliente}`, { headers })
      .then(({ data }) => setSedes(data || []))
      .catch(() => setSedes([]));
    // eslint-disable-next-line
  }, [form.cliente]);

  useEffect(() => {
    if (!form.cliente) {
      setAreas([]);
      return;
    }

    const sede = form.sede || "all";
    axios
      .get(`${API}/areas/con_equipos?cliente=${form.cliente}&sede=${sede}`, {
        headers,
      })
      .then(({ data }) => setAreas(data || []))
      .catch(() => setAreas([]));
    // eslint-disable-next-line
  }, [form.cliente, form.sede]);

  useEffect(() => {
    if (!form.area) {
      setEquipos([]);
      setForm((prev) => ({ ...prev, equipo: "" }));
      return;
    }

    const sedeQuery = form.sede ? `&sede=${form.sede}` : "";
    axios
      .get(`${API}/equipos?area=${form.area}${sedeQuery}`, { headers })
      .then(({ data }) => {
        const equiposData = Array.isArray(data) ? data : data?.equipos || [];
        setEquipos(equiposData);
      })
      .catch(() => setEquipos([]));
    // eslint-disable-next-line
  }, [form.area, form.sede]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("sol_")) {
      const field = name.replace("sol_", "");
      setForm((prev) => ({
        ...prev,
        solicitante: { ...prev.solicitante, [field]: value },
      }));
      return;
    }

    setForm((prev) => {
      if (name === "cliente") {
        return { ...prev, cliente: value, sede: "", area: "", equipo: "" };
      }
      if (name === "sede") {
        return { ...prev, sede: value, area: "", equipo: "" };
      }
      if (name === "area") {
        return { ...prev, area: value, equipo: "" };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.cliente ||
      !form.solicitante.nombre ||
      !form.tipo ||
      !form.asunto
    ) {
      return toast.error("Completa los campos obligatorios");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/soporte`, form, { headers });
      onCreado();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al crear ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-xl">
              <Headphones className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Nuevo ticket de soporte
              </h2>
              <p className="text-xs text-gray-400">
                Se generará código automáticamente
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" /> Ubicación del requerimiento
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Cliente *"
                name="cliente"
                value={form.cliente}
                options={clientes}
                onChange={handleChange}
                placeholder="Seleccionar cliente"
              />

              <SelectField
                label="Sede"
                name="sede"
                value={form.sede}
                options={sedes}
                onChange={handleChange}
                placeholder="Seleccionar sede"
              />

              <SelectField
                label="Área"
                name="area"
                value={form.area}
                options={areas}
                onChange={handleChange}
                placeholder="Seleccionar área"
              />

              <SelectField
                label="Equipo (opcional)"
                name="equipo"
                value={form.equipo}
                options={equipos}
                onChange={handleChange}
                placeholder="Sin equipo asociado"
                displayFn={(eq) =>
                  `[${eq.serial}] ${eq.marca?.name} ${eq.modelo?.name}`
                }
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" /> Clasificación
            </p>
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Tipo *"
                name="tipo"
                value={form.tipo}
                options={TIPOS.map((t) => ({ _id: t, name: t }))}
                onChange={handleChange}
                valueKey="_id"
              />

              <SelectField
                label="Prioridad"
                name="prioridad"
                value={form.prioridad}
                options={PRIORIDADES.map((p) => ({ _id: p, name: p }))}
                onChange={handleChange}
                valueKey="_id"
              />
            </div>
          </div>

          <div>
            <div className="mb-3">
              <InputField
                label="Asunto *"
                type="text"
                name="asunto"
                value={form.asunto}
                onChange={handleChange}
                required
                placeholder="Resumen breve del requerimiento"
              />
            </div>
            <TextAreaField
              label="Descripción detallada"
              name="descripcion"
              value={form.descripcion}
              onChange={handleChange}
              rows={3}
              placeholder="Describe el problema o solicitud con detalle..."
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1">
              <User className="h-3.5 w-3.5" /> Datos del solicitante
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                label="Nombre *"
                type="text"
                name="sol_nombre"
                value={form.solicitante.nombre}
                onChange={handleChange}
                required
                placeholder="Nombre completo"
              />

              <InputField
                label="Cargo"
                type="text"
                name="sol_cargo"
                value={form.solicitante.cargo}
                onChange={handleChange}
                placeholder="Cargo o área"
              />

              <InputField
                label="Email"
                type="email"
                name="sol_email"
                value={form.solicitante.email}
                onChange={handleChange}
                placeholder="correo@ejemplo.com"
              />

              <InputField
                label="Teléfono"
                type="text"
                name="sol_telefono"
                value={form.solicitante.telefono}
                onChange={handleChange}
                placeholder="+57 300 0000000"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? "Creando..." : "Crear ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
